import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, onSnapshot, query, orderBy, doc, updateDoc, serverTimestamp, where } from 'firebase/firestore';

const S = {
  page: { padding: '0 24px 24px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 16px' },
  title: { fontSize: 20, fontWeight: 900, color: '#1A1A2E' },
  sub: { fontSize: 12, color: '#9B8F7A', marginTop: 2 },
  btn: (color) => ({ background: color || '#1E3A5F', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }),
  card: { background: '#FFFFFF', border: '1px solid #E2DDD6', borderRadius: 12, padding: 20 },
  label: { fontSize: 11, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6, display: 'block', marginTop: 14 },
  input: { background: '#1E3A5F22', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  select: { background: '#F4F2EE', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  textarea: { background: '#1E3A5F22', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', minHeight: 80, resize: 'vertical' },
  badge: (color) => ({ background: color + '22', color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block' }),
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  alertCard: (color, urgent) => ({ background: '#FFFFFF', border: `2px solid ${color}`, borderRadius: 12, padding: 20, position: 'relative', animation: urgent ? 'pulse 2s infinite' : 'none' }),
  timelineItem: { display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #1E3A5F22' },
  timelineDot: (color) => ({ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 4 }),
  responseBtn: (color, selected) => ({ background: selected ? color : color + '22', color: selected ? '#fff' : color, border: `1px solid ${color}44`, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }),
  channelBtn: (active) => ({ background: active ? '#1E3A5F' : 'transparent', border: '1px solid #1E3A5F44', color: active ? '#fff' : '#9CA3AF', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 400, display: 'flex', alignItems: 'center', gap: 6 }),
};

const ALERT_TYPES = [
  { id: 'fire', label: '🔥 Fire', color: '#D32F2F' },
  { id: 'evacuation', label: '🚪 Evacuation', color: '#F57F17' },
  { id: 'medical', label: '🏥 Medical', color: '#1565C0' },
  { id: 'intruder', label: '🚨 Intruder', color: '#8B0000' },
  { id: 'protest', label: '📢 Protest', color: '#F57F17' },
  { id: 'suspicious_package', label: '📦 Suspicious Package', color: '#7B1FA2' },
  { id: 'power_failure', label: '⚡ Power Failure', color: '#455A64' },
  { id: 'custom', label: '✏️ Custom', color: '#1E3A5F' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: '#2E7D32' },
  { id: 'medium', label: 'Medium', color: '#F57F17' },
  { id: 'high', label: 'High', color: '#D32F2F' },
  { id: 'critical', label: 'CRITICAL', color: '#8B0000' },
];

const CHANNELS = [
  { id: 'push', label: 'Push notification', icon: '🔔' },
  { id: 'inapp', label: 'In-app', icon: '📱' },
  { id: 'email', label: 'Email', icon: '✉️' },
  { id: 'sms', label: 'SMS', icon: '💬' },
  { id: 'webhook', label: 'Teams/Slack', icon: '🔗' },
];

const STAFF_GROUPS = ['All staff', 'Security officers', 'Supervisors', 'Management', 'Fire wardens', 'First aiders'];

const priorityColor = (p) => {
  const map = { low: '#2E7D32', medium: '#F57F17', high: '#D32F2F', critical: '#8B0000' };
  return map[p] || '#1E3A5F';
};

const alertTypeColor = (t) => {
  const found = ALERT_TYPES.find(a => a.id === t);
  return found?.color || '#1E3A5F';
};

const formatTime = (ts) => {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
};

export default function EmergencyPage({ tenantId, users }) {
  const [tab, setTab] = useState('active');
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Keep selectedAlert in sync with live data
  useEffect(() => {
    if (!selectedAlert) return;
    const live = alerts.find(a => a._id === selectedAlert._id);
    if (live) setSelectedAlert(live);
  }, [alerts]);
  const [creating, setCreating] = useState(false);
useEffect(() => {
    if (!selectedAlert) return;
    const live = alerts.find(a => a._id === selectedAlert._id);
    if (live) setSelectedAlert(live);
  }, [alerts]);

  const [form, setForm] = useState({
    type: 'fire',
    priority: 'high',
    site: '',
    message: '',
    customType: '',
    channels: ['push', 'inapp'],
    staffGroups: ['All staff'],
  });

  useEffect(() => {
    const q = query(
      collection(db, 'emergency_alerts'),
      where('tenant_id', '==', tenantId),
      orderBy('created_at', 'desc')
    );
    return onSnapshot(q, snap => {
      setAlerts(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
    });
  }, [tenantId]);

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleChannel = (ch) => {
    setF('channels', form.channels.includes(ch)
      ? form.channels.filter(c => c !== ch)
      : [...form.channels, ch]);
  };

  const toggleGroup = (g) => {
    setF('staffGroups', form.staffGroups.includes(g)
      ? form.staffGroups.filter(x => x !== g)
      : [...form.staffGroups, g]);
  };

  const sendAlert = async (e) => {
    e.preventDefault();
    if (!form.site.trim() || !form.message.trim()) return;
    setCreating(true);
    try {
      const user = auth.currentUser;
      const alertType = form.type === 'custom' ? form.customType : ALERT_TYPES.find(a => a.id === form.type)?.label;
      const alertRef = await addDoc(collection(db, 'emergency_alerts'), {
        type: form.type,
        type_label: alertType,
        priority: form.priority,
        site: form.site,
        message: form.message,
        channels: form.channels,
        staff_groups: form.staffGroups,
        tenant_id: tenantId,
        created_by: user?.email,
        created_by_uid: user?.uid,
        created_at: serverTimestamp(),
        sent_at: serverTimestamp(),
        status: 'active',
        responses: {},
        acknowledged_count: 0,
        safe_count: 0,
        assistance_count: 0,
        not_on_site_count: 0,
        timeline: [{
          action: 'Alert created and sent',
          by: user?.email,
          time: new Date().toISOString(),
          type: 'created',
        }],
      });

      // Save to notifications collection for push delivery
      await addDoc(collection(db, 'notifications'), {
        type: 'emergency',
        alert_id: alertRef.id,
        title: `🚨 ${alertType} — ${form.site}`,
        body: form.message,
        priority: form.priority,
        tenant_id: tenantId,
        sent_at: serverTimestamp(),
        read: false,
      });

      setShowCreate(false);
      setForm({ type: 'fire', priority: 'high', site: '', message: '', customType: '', channels: ['push', 'inapp'], staffGroups: ['All staff'] });
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const resolveAlert = async (alert) => {
    if (!alert._id) return;
    await updateDoc(doc(db, 'emergency_alerts', alert._id), {
      status: 'resolved',
      resolved_at: serverTimestamp(),
      resolved_by: auth.currentUser?.email,
      timeline: [...(alert.timeline || []), {
        action: 'Alert resolved',
        by: auth.currentUser?.email,
        time: new Date().toISOString(),
        type: 'resolved',
      }],
    });
    if (selectedAlert?._id === alert._id) setSelectedAlert(null);
  };

  const activeAlerts = alerts.filter(a => a.status === 'active');
  const resolvedAlerts = alerts.filter(a => a.status === 'resolved');

  return (
    <div style={S.page}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }
        .pulse-border { animation: pulse 2s infinite; }
      `}</style>

      <div style={S.header}>
        <div>
          <div style={S.title}>Emergency Command</div>
          <div style={S.sub}>Create alerts, track staff accountability, monitor responses</div>
        </div>
        <button style={{ ...S.btn('#8B0000'), fontSize: 14, padding: '10px 20px' }}
          onClick={() => setShowCreate(true)}>
          🚨 Create Emergency Alert
        </button>
      </div>

      {/* Active alert banner */}
      {activeAlerts.length > 0 && (
        <div style={{ background: '#8B000022', border: '2px solid #8B0000', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 24 }}>🚨</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#D32F2F' }}>{activeAlerts.length} ACTIVE EMERGENCY ALERT{activeAlerts.length > 1 ? 'S' : ''}</div>
              <div style={{ fontSize: 12, color: '#9B8F7A' }}>{activeAlerts[0]?.type_label} at {activeAlerts[0]?.site}</div>
            </div>
          </div>
          <button style={S.btn('#D32F2F')} onClick={() => setTab('active')}>View alerts →</button>
        </div>
      )}

      {/* Stats */}
      <div style={{ ...S.grid3, marginBottom: 20 }}>
        <div style={S.card}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>ACTIVE ALERTS</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: activeAlerts.length > 0 ? '#D32F2F' : '#2E7D32' }}>{activeAlerts.length}</div>
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>TOTAL ALERTS</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#1A1A2E' }}>{alerts.length}</div>
        </div>
        <div style={S.card}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>RESOLVED</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#2E7D32' }}>{resolvedAlerts.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['active', 'resolved', 'create'].map(t => (
          <button key={t} style={S.channelBtn(tab === t)} onClick={() => { setTab(t); if (t === 'create') setShowCreate(true); }}>
            {t === 'active' ? `🔴 Active (${activeAlerts.length})` : t === 'resolved' ? `✓ Resolved (${resolvedAlerts.length})` : '+ New Alert'}
          </button>
        ))}
      </div>

      {/* Alert list + detail */}
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1 }}>
          {(tab === 'active' ? activeAlerts : resolvedAlerts).map((alert, i) => (
            <div key={i} style={{ ...S.alertCard(priorityColor(alert.priority), alert.status === 'active' && alert.priority === 'critical'), marginBottom: 12, cursor: 'pointer' }}
              onClick={() => setSelectedAlert(alert)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#1A1A2E', marginBottom: 2 }}>{alert.type_label}</div>
                  <div style={{ fontSize: 13, color: '#9B8F7A' }}>{alert.site}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={S.badge(priorityColor(alert.priority))}>{alert.priority?.toUpperCase()}</span>
                  <span style={S.badge(alert.status === 'active' ? '#D32F2F' : '#2E7D32')}>{alert.status}</span>
                </div>
              </div>

              <div style={{ fontSize: 13, color: '#9B8F7A', marginBottom: 12, lineHeight: 1.5 }}>{alert.message}</div>

              {/* Response counts */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: '#2E7D32' }}>✓ Safe: {alert.safe_count || 0}</div>
                <div style={{ fontSize: 12, color: '#D32F2F' }}>⚠ Need help: {alert.assistance_count || 0}</div>
                <div style={{ fontSize: 12, color: '#9B8F7A' }}>Not on site: {alert.not_on_site_count || 0}</div>
                <div style={{ fontSize: 12, color: '#C9A84C' }}>Acknowledged: {alert.acknowledged_count || 0}</div>
              </div>

              {/* Channels */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {(alert.channels || []).map(ch => (
                  <span key={ch} style={S.badge('#1E3A5F')}>{CHANNELS.find(c => c.id === ch)?.icon} {ch}</span>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: '#9B8F7A' }}>By {alert.created_by} · {formatTime(alert.created_at)}</div>
                {alert.status === 'active' && (
                  <button style={{ ...S.btn('#2E7D32'), padding: '6px 12px', fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); resolveAlert(alert); }}>
                    ✓ Resolve
                  </button>
                )}
              </div>
            </div>
          ))}

          {(tab === 'active' ? activeAlerts : resolvedAlerts).length === 0 && (
            <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#9B8F7A' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{tab === 'active' ? '✅' : '📋'}</div>
              <div>{tab === 'active' ? 'No active alerts — all clear.' : 'No resolved alerts yet.'}</div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedAlert && (
          <div style={{ ...S.card, width: 360, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 80 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#1A1A2E' }}>Alert detail</div>
              <button onClick={() => setSelectedAlert(null)} style={{ background: 'none', border: 'none', color: '#9B8F7A', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <span style={S.badge(priorityColor(selectedAlert.priority))}>{selectedAlert.priority?.toUpperCase()}</span>
            <span style={{ ...S.badge(alertTypeColor(selectedAlert.type)), marginLeft: 6 }}>{selectedAlert.type_label}</span>

            <div style={{ marginTop: 14, fontSize: 11, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Message</div>
            <div style={{ fontSize: 13, color: '#1A1A2E', lineHeight: 1.6, marginTop: 6 }}>{selectedAlert.message}</div>

            <div style={{ marginTop: 14, fontSize: 11, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Staff accountability</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              {[
                { label: 'I am safe', count: selectedAlert.safe_count || 0, color: '#2E7D32' },
                { label: 'Need assistance', count: selectedAlert.assistance_count || 0, color: '#D32F2F' },
                { label: 'Not on site', count: selectedAlert.not_on_site_count || 0, color: '#9B8F7A' },
                { label: 'Acknowledged', count: selectedAlert.acknowledged_count || 0, color: '#C9A84C' },
              ].map((r, i) => (
                <div key={i} style={{ background: r.color + '22', border: `1px solid ${r.color}44`, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: r.color }}>{r.count}</div>
                  <div style={{ fontSize: 11, color: '#9B8F7A' }}>{r.label}</div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div style={{ marginTop: 16, fontSize: 11, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>Timeline</div>
            {(selectedAlert.timeline || []).map((event, i) => (
              <div key={i} style={S.timelineItem}>
                <div style={S.timelineDot(event.type === 'created' ? '#2E7D32' : event.type === 'resolved' ? '#C9A84C' : '#1E3A5F')} />
                <div>
                  <div style={{ fontSize: 12, color: '#1A1A2E', fontWeight: 600 }}>{event.action}</div>
                  <div style={{ fontSize: 11, color: '#9B8F7A' }}>{event.by} · {event.time ? new Date(event.time).toLocaleTimeString('en-GB') : ''}</div>
                </div>
              </div>
            ))}

            {selectedAlert.status === 'active' && (
              <button style={{ ...S.btn('#2E7D32'), width: '100%', marginTop: 16 }}
                onClick={() => resolveAlert(selectedAlert)}>✓ Resolve alert</button>
            )}
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0A0E1Aee', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#FFFFFF', border: '2px solid #8B0000', borderRadius: 12, padding: 28, width: 600, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#D32F2F' }}>🚨 Create Emergency Alert</div>
                <div style={{ fontSize: 12, color: '#9B8F7A', marginTop: 2 }}>This will immediately notify selected staff</div>
              </div>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', color: '#9B8F7A', cursor: 'pointer', fontSize: 20 }}>✕</button>
            </div>

            <form onSubmit={sendAlert}>
              {/* Alert type */}
              <label style={S.label}>Alert type</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 4 }}>
                {ALERT_TYPES.map(t => (
                  <button key={t.id} type="button"
                    style={{ background: form.type === t.id ? t.color + '33' : 'transparent', border: `2px solid ${form.type === t.id ? t.color : '#1E3A5F44'}`, color: form.type === t.id ? t.color : '#9CA3AF', padding: '8px 6px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: form.type === t.id ? 700 : 400, textAlign: 'center' }}
                    onClick={() => setF('type', t.id)}>{t.label}</button>
                ))}
              </div>

              {form.type === 'custom' && (
                <>
                  <label style={S.label}>Custom alert name</label>
                  <input style={S.input} placeholder="e.g. Gas leak" value={form.customType} onChange={e => setF('customType', e.target.value)} />
                </>
              )}

              {/* Priority */}
              <label style={S.label}>Priority</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {PRIORITIES.map(p => (
                  <button key={p.id} type="button"
                    style={{ background: form.priority === p.id ? p.color : 'transparent', border: `2px solid ${form.priority === p.id ? p.color : p.color + '44'}`, color: form.priority === p.id ? '#fff' : p.color, padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, flex: 1 }}
                    onClick={() => setF('priority', p.id)}>{p.label}</button>
                ))}
              </div>

              {/* Site */}
              <label style={S.label}>Building / Site / Location</label>
              <input style={S.input} placeholder="e.g. Paddington Square — Floor 3" value={form.site} onChange={e => setF('site', e.target.value)} required />

              {/* Message */}
              <label style={S.label}>Alert message</label>
              <textarea style={S.textarea} placeholder="Describe the emergency and any instructions for staff..." value={form.message} onChange={e => setF('message', e.target.value)} required />

              {/* Channels */}
              <label style={S.label}>Send via</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CHANNELS.map(ch => (
                  <button key={ch.id} type="button" style={S.channelBtn(form.channels.includes(ch.id))}
                    onClick={() => toggleChannel(ch.id)}>
                    {ch.icon} {ch.label}
                  </button>
                ))}
              </div>

              {/* Staff groups */}
              <label style={S.label}>Send to</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STAFF_GROUPS.map(g => (
                  <button key={g} type="button"
                    style={{ background: form.staffGroups.includes(g) ? '#1E3A5F' : 'transparent', border: '1px solid #1E3A5F44', color: form.staffGroups.includes(g) ? '#fff' : '#9CA3AF', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: form.staffGroups.includes(g) ? 700 : 400 }}
                    onClick={() => toggleGroup(g)}>{g}</button>
                ))}
              </div>

              {/* Warning */}
              <div style={{ background: '#8B000022', border: '1px solid #8B000044', borderRadius: 8, padding: 12, marginTop: 16, fontSize: 12, color: '#D32F2F' }}>
                ⚠ This alert will be sent immediately to all selected staff groups via chosen channels.
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="button" onClick={() => setShowCreate(false)} style={{ ...S.btn('#1E3A5F44'), flex: 1, color: '#9B8F7A' }}>Cancel</button>
                <button type="submit" disabled={creating} style={{ ...S.btn('#8B0000'), flex: 2, fontSize: 14 }}>
                  {creating ? 'Sending…' : '🚨 Send Emergency Alert'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}